import { type LoaderFunctionArgs, json, redirect } from "@remix-run/node"
import {
	Link,
	NavLink,
	Outlet,
	useLoaderData,
	useMatches,
	useRouteLoaderData,
} from "@remix-run/react"
import { and, eq, ne } from "drizzle-orm"
import { userAdmin } from "drizzle/user-admin.sql"
import { userSocialChannel } from "drizzle/user-social-channel.sql"
import { user } from "drizzle/user.sql"
import { z } from "zod"
import { Avatar } from "~/components/Avatar"
import { Button } from "~/components/Button"
import { PointsCount } from "~/components/PointsCount"
import { APP_LOGO_IMAGE, ENABLE_SHOP } from "~/constants"
import { auth } from "~/services/auth.server"

export async function loader({ request, context }: LoaderFunctionArgs) {
	const { authenticator, getSession, destroySession, commitSession } =
		await auth(context)
	const userSession = await authenticator.isAuthenticated(request)

	if (userSession?.userId) {
		const [userData] = await context.db
			.select({
				userId: user.userId,
				points: user.points,
				totalEarnedPoints: user.totalEarnedPoints,
				userAdminId: userAdmin.userAdminId,
				profileImageUrl: user.profileImageUrl,
				displayName: user.displayName,
			})
			.from(user)
			.leftJoin(userAdmin, eq(userAdmin.userId, user.userId))
			.where(eq(user.userId, userSession.userId))
			.limit(1)

		if (!userData) {
			const session = await getSession(request.headers.get("Cookie"))
			await destroySession(session)
			throw redirect("/", {
				headers: {
					"Set-Cookie": await commitSession(session),
				},
			})
		}

		const userSocialChannels = await context.db
			.select({
				userSocialChannelId: userSocialChannel.userSocialChannelId,
				channelName: userSocialChannel.channelName,
				handle: userSocialChannel.handle,
			})
			.from(userSocialChannel)
			.where(
				and(
					eq(userSocialChannel.userId, userSession.userId),
					ne(userSocialChannel.handle, ""),
				),
			)

		return json({
			points: userData.points,
			totalEarnedPoints: userData.totalEarnedPoints,
			isAdmin: Boolean(userData.userAdminId),
			userId: userData.userId,
			userSocialChannels,
			profileImageUrl: userData.profileImageUrl,
			displayName: userData.displayName,
		})
	}

	return json(null)
}

export function useUser() {
	const userData = useRouteLoaderData<typeof loader>("index/index.layout")
	return userData ?? null
}

export default function IndexLayout() {
	const userData = useLoaderData<typeof loader>()

	return (
		<>
			<nav className="flex items-center justify-between gap-4 px-4 py-3.5">
				<NavLink to="/" className="block">
					<img
						src={APP_LOGO_IMAGE}
						alt="Logo"
						className="h-14 w-auto object-contain"
					/>
				</NavLink>

				{userData?.isAdmin ? (
					<Button asChild>
						<Link
							to="/admin"
						>
							Admin
						</Link>
					</Button>
				) : null}

				{userData ? (
					<NavLink to="/settings" className="flex items-center gap-3">
						<PointsCount points={userData.points} />
						<Avatar
							alt={userData.displayName ?? ""}
							src={userData.profileImageUrl ?? ""}
							className="size-8"
						/>
					</NavLink>
				) : null}
			</nav>
			<main>
				<Outlet />
			</main>
			<BottomNav />
		</>
	)
}

const handleSchema = z.object({
	showBottomNav: z.boolean().optional(),
	isHomeRoute: z.boolean().optional(),
})

function BottomNav() {
	const matches = useMatches()
	const leafHandle = matches.at(-1)?.handle
	const parsedLeafHandle = handleSchema.safeParse(leafHandle)

	if (!parsedLeafHandle.success) {
		return null
	}

	const shouldShow = parsedLeafHandle.data.showBottomNav
	const isHomeRoute = parsedLeafHandle.data.isHomeRoute

	if (!shouldShow) {
		return null
	}

	return (
		<nav className="sticky bottom-4 grid w-full max-w-[296px] auto-cols-fr grid-flow-col justify-center justify-self-center overflow-hidden rounded-[3px] bg-navigation-background text-center leading-tight shadow-sm">
			<NavLink
				data-home-route={isHomeRoute}
				className="py-3.5 text-navigation-foreground [&.active]:text-navigation-foreground-active data-[home-route=true]:text-navigation-foreground-active"
				to="/home"
			>
				Home
			</NavLink>
			<NavLink
				className="py-3.5 text-navigation-foreground [&.active]:text-navigation-foreground-active"
				to="/quests"
			>
				Quests
			</NavLink>
			{ENABLE_SHOP && (
				<NavLink
					className="py-3.5 text-navigation-foreground [&.active]:text-navigation-foreground-active"
					to="/items"
				>
					Shop
				</NavLink>
			)}
		</nav>
	)
}
